import { useMutation } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from './useCurrentUser';
import { DISCOVERY_RELAYS, discoverySignal } from '@/lib/appRelays';
import {
  KEY_BACKUP_KIND,
  KEY_BACKUP_TAG,
  KEY_BACKUP_D_PREFIX,
} from '@/lib/directListing';
import { ADMIN_PUBKEY } from '@/lib/admin';

/**
 * Admin-only: fetch and decrypt the stored recovery key (nsec) for a direct
 * listing.
 *
 * The backup was NIP-44 encrypted between the listing key and the admin
 * pubkey, so the admin's signer can decrypt it with the listing pubkey as the
 * peer. Runs on demand (mutation) so the key is only decrypted when explicitly
 * requested — never eagerly.
 */
export function useRevealListingKey() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (listingPubkey: string): Promise<string> => {
      if (!user) throw new Error('Not logged in');
      if (user.pubkey.toLowerCase() !== ADMIN_PUBKEY) {
        throw new Error('Only the admin can reveal recovery keys');
      }
      if (!user.signer.nip44) {
        throw new Error('Your signer does not support NIP-44 decryption');
      }

      const biz = listingPubkey.toLowerCase();
      const events = await nostr.group(DISCOVERY_RELAYS).query(
        [{
          kinds: [KEY_BACKUP_KIND],
          authors: [biz],
          '#d': [`${KEY_BACKUP_D_PREFIX}${biz}`],
          '#t': [KEY_BACKUP_TAG],
          limit: 1,
        }],
        { signal: discoverySignal() },
      );

      if (events.length === 0) {
        throw new Error('No stored recovery key found for this listing.');
      }

      // Decrypt with the listing pubkey as the peer (symmetric conversation key).
      const nsec = await user.signer.nip44.decrypt(biz, events[0].content);
      if (!nsec.startsWith('nsec1')) {
        throw new Error('Decrypted value was not a valid key.');
      }
      return nsec;
    },
  });
}
